'use client';

import { useEffect } from 'react';
import { subscribeToConversions } from '@highereducation/dynamic-experience';
import { newrelic } from '@hen-site/lib/newrelic/client';
import type { PaidSocialEmitter } from '@hen-site/lib/paid-social/types';

const isLeadConversion = (
  conversion: unknown,
): conversion is { name: 'Lead'; pageUrl: string } => {
  if (conversion === null || typeof conversion !== 'object') {
    return false;
  }

  const candidate = conversion as { name?: unknown; pageUrl?: unknown };
  return (
    candidate.name === 'Lead' &&
    typeof candidate.pageUrl === 'string' &&
    candidate.pageUrl.length > 0
  );
};

/**
 * Forwards Dynamic Experience conversions onto the paid-social emit seam.
 *
 * Subscribe from the tenant-level bootstrap so a lead is reported once no
 * matter how many experiences the page renders. Consent is enforced by the
 * emitter. Malformed window events and vendor throws are swallowed here so a
 * successful lead submit can still navigate.
 *
 * @param {PaidSocialEmitter | null} emitter - Active seam, or null.
 */
export const usePaidSocialConversions = (
  emitter: PaidSocialEmitter | null,
): void => {
  useEffect(() => {
    if (!emitter) {
      return;
    }

    return subscribeToConversions((conversion) => {
      if (!isLeadConversion(conversion)) {
        return;
      }

      try {
        emitter.trackConversion(conversion);
      } catch (error) {
        newrelic.noticeError?.(
          new Error('PaidSocialConversionFailed', { cause: error }),
        );
      }
    });
  }, [emitter]);
};
